/**
 * measureMedia.js
 *
 * Stamp every media node with its file's natural pixel dimensions so the
 * presentor can choose a layout the instant a stimulus arrives, instead of
 * waiting for the file to load and then re-arranging around it.
 *
 * Handles images and silent looping video. Both are laid out identically — the
 * arrangement comes from the shape of the file, not its kind.
 *
 * This runs at compile time — immediately before buildDescriptorJSON, on both
 * Save to Cloud and Export. Compile time rather than upload time on purpose:
 * a node's src can also arrive by pasted URL or by importing a survey JSON,
 * and only the compile step sees all three.
 *
 * buildDescriptorJSON is synchronous and shared with the server-side compiler,
 * so the measuring lives out here rather than inside it.
 *
 * The presentor needs the aspect ratio, not the absolute size — image quality
 * at display size is the survey author's responsibility (see the media node
 * section of docs/cloudsam_survey_designer.md).
 */

// A slow or unreachable image must not hold up a save. Nodes that time out are
// left unstamped; the presentor falls back to measuring on load for those.
const MEASURE_TIMEOUT_MS = 5000;

/**
 * Is this media node a video?
 *
 * `type` is stamped at upload, but a src pasted as a URL or a node authored
 * before that field existed carries nothing, so the mime type and the file
 * extension are the fallbacks. Mirrors stimulusIsVideo in newPresentor-app.js —
 * if one changes, change the other, or the designer measures a clip with an
 * Image element and silently stamps nothing.
 */
export function isVideoMedia(media) {
    if (!media) return false;
    if (String(media.type || '').toLowerCase() === 'video') return true;
    if (String(media.mime_type || '').toLowerCase().indexOf('video/') === 0) return true;
    const src = String(media.src || '').toLowerCase();
    if (src.indexOf('data:video/') === 0) return true;
    const path = src.split('?')[0].split('#')[0];
    return /\.(mp4|webm|ogv|mov|m4v)$/.test(path);
}

/**
 * Read one video's natural dimensions.
 *
 * Video reports its size as videoWidth/videoHeight once metadata arrives, not
 * naturalWidth on load — an Image element never fires for a clip at all.
 * preload='metadata' fetches only the header, so this costs a few kilobytes
 * rather than the whole file.
 *
 * @returns {Promise<{width:number,height:number}|null>} null if it can't be read.
 */
export function measureVideo(src, timeoutMs = MEASURE_TIMEOUT_MS) {
    return new Promise((resolve) => {
        if (!src || typeof src !== 'string') {
            resolve(null);
            return;
        }
        let settled = false;
        const finish = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            try { video.removeAttribute('src'); video.load(); } catch (_) { }
            resolve(value);
        };
        const timer = setTimeout(() => finish(null), timeoutMs);
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.onloadedmetadata = () => {
            const width = video.videoWidth;
            const height = video.videoHeight;
            finish(width > 0 && height > 0 ? { width, height } : null);
        };
        video.onerror = () => finish(null);
        video.src = src;
    });
}

/**
 * Read one image's natural dimensions.
 * @returns {Promise<{width:number,height:number}|null>} null if it can't be read.
 */
export function measureImage(src, timeoutMs = MEASURE_TIMEOUT_MS) {
    return new Promise((resolve) => {
        if (!src || typeof src !== 'string') {
            resolve(null);
            return;
        }
        let settled = false;
        const finish = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(value);
        };
        const timer = setTimeout(() => finish(null), timeoutMs);
        const img = new Image();
        // Dimensions are readable cross-origin; only pixel data is restricted.
        img.onload = () => {
            const width = img.naturalWidth;
            const height = img.naturalHeight;
            finish(width > 0 && height > 0 ? { width, height } : null);
        };
        img.onerror = () => finish(null);
        img.src = src;
    });
}

/**
 * Stamp natural_width / natural_height onto every media node that has a src.
 *
 * Returns a new array; nodes are copied only where something changed, so the
 * common re-save case leaves object identity alone for everything else.
 * Measurements are re-taken on every compile so that swapping the image behind
 * an unchanged src still lands correct numbers.
 *
 * @param {Array} nodes - designer nodes (React Flow shape: { type, data })
 * @returns {Promise<Array>} nodes with media dimensions stamped
 */
export async function stampMediaDimensions(nodes) {
    if (!Array.isArray(nodes)) return nodes;

    const targets = [];
    nodes.forEach((node, index) => {
        if (node?.type !== 'media') return;
        const media = node?.data?.media;
        const src = media?.src;
        if (src) targets.push({ index, src, isVideo: isVideoMedia(media) });
    });
    if (targets.length === 0) return nodes;

    // Measure distinct sources once — the same file is often shown and hidden
    // by a paired node, or reused across several stimuli. One src is always one
    // kind of file, so the video flag rides along with it.
    const distinct = new Map();
    targets.forEach((t) => { if (!distinct.has(t.src)) distinct.set(t.src, t.isVideo); });

    const bySrc = new Map();
    await Promise.all(
        [...distinct.entries()].map(async ([src, isVideo]) => {
            bySrc.set(src, await (isVideo ? measureVideo(src) : measureImage(src)));
        }),
    );

    const next = [...nodes];
    for (const { index, src } of targets) {
        const measured = bySrc.get(src);
        if (!measured) continue;
        const node = next[index];
        const media = { ...(node.data.media || {}) };
        if (media.natural_width === measured.width && media.natural_height === measured.height) {
            continue;
        }
        media.natural_width = measured.width;
        media.natural_height = measured.height;
        next[index] = { ...node, data: { ...node.data, media } };
    }
    return next;
}

export default stampMediaDimensions;
